import React, {
  useRef,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { Container, Graphics } from "@pixi/react";
import anime from "animejs";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";
import SliderButton from "../Buttons/SliderButton";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import SymbolsSlide from "./SymbolsSlide";
import PaylinesSlide from "./PaylinesSlide";
import FeaturesSlide from "./FeaturesSlide";

const ViewportMask = React.forwardRef(({ width, height }, ref) => (
  <Graphics
    ref={ref}
    name="paytable_viewport_mask"
    draw={(g) => {
      g.clear();
      g.beginFill(0x000000, 1);
      g.drawRect(-width / 2, 0, width, height);
      g.endFill();
    }}
  />
));

const PaytableSlider = ({ viewportConfig, totalSlides = 1 }) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const slidesContainerRef = useRef(null);
  const viewportMaskRef = useRef(null);
  const animationRef = useRef(null);
  const isJumpingRef = useRef(false);
  const { getResponsiveConfig, device, orientation, uiSymbolScale } =
    useDimensions();

  // Get button config for pagination positioning
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG),
    [getResponsiveConfig]
  );

  // Helper function to determine slide component and props based on actual slide index
  const getSlideInfo = useCallback((slideIndex) => {
    let SlideComponent, slideType;

    if (slideIndex <= 1) {
      SlideComponent = SymbolsSlide;
      slideType = "symbols";
    } else if (slideIndex <= 4) {
      SlideComponent = FeaturesSlide;
      slideType = "features";
    } else {
      SlideComponent = PaylinesSlide;
      slideType = "paylines";
    }

    return { SlideComponent, slideType, slideIndex };
  }, []);

  // Calculate slide x position
  const getSlideX = useCallback(
    (position) =>
      Math.round(viewportConfig.maxWidth / 2 + position * viewportConfig.maxWidth),
    [viewportConfig.maxWidth]
  );

  // Render a slide container
  const renderSlide = useCallback(
    (slideIndex, position, key) => {
      const { SlideComponent, slideType } = getSlideInfo(slideIndex);
      return (
        <Container key={key} name={`paytable_slide_${key}`} x={getSlideX(position)} y={0}>
          <SlideComponent
            viewportConfig={viewportConfig}
            slideIndex={slideIndex}
            slideType={slideType}
          />
        </Container>
      );
    },
    [getSlideInfo, getSlideX, viewportConfig]
  );

  // Calculate slides container position based on current slide
  // Account for the leading clone slide (shift by +1)
  const slidesContainerX = useMemo(
    () =>
      -viewportConfig.maxWidth / 2 -
      (currentSlideIndex + 1) * viewportConfig.maxWidth,
    [currentSlideIndex, viewportConfig.maxWidth]
  );

  // Handle next slide (circular with clone slides)
  const handleNext = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlideIndex((prev) => prev + 1);
  }, [isTransitioning]);

  // Handle previous slide (circular with clone slides)
  const handlePrevious = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlideIndex((prev) => prev - 1);
  }, [isTransitioning]);

  // Handle pagination dot click
  const handleDotClick = useCallback((index) => {
    setCurrentSlideIndex(index);
  }, []);

  // Animate slides container position with elastic bounce effect
  useEffect(() => {
    if (slidesContainerRef.current && animationRef.current) {
      anime.remove(animationRef.current);
    }

    if (slidesContainerRef.current) {
      // Check if we're jumping from clone to real slide
      if (isJumpingRef.current) {
        // Instant jump without animation
        slidesContainerRef.current.x = slidesContainerX;
        isJumpingRef.current = false;
        setIsTransitioning(false);
      } else {
        // Normal animation
        animationRef.current = anime({
          targets: slidesContainerRef.current,
          x: slidesContainerX,
          duration: 650,
          easing: "easeOutElastic(0.5, 1.2)",
          complete: () => {
            // After animation completes, check if we're on a clone and jump to real slide
            if (currentSlideIndex >= totalSlides) {
              // On clone of first slide (after last slide), jump to real first slide
              isJumpingRef.current = true;
              setCurrentSlideIndex(0);
            } else if (currentSlideIndex < 0) {
              // On clone of last slide (before first slide), jump to real last slide
              isJumpingRef.current = true;
              setCurrentSlideIndex(totalSlides - 1);
            } else {
              // Normal slide, allow next transition
              setIsTransitioning(false);
            }
          },
        });
      }
    }

    return () => {
      if (animationRef.current) {
        anime.remove(animationRef.current);
      }
    };
  }, [slidesContainerX, currentSlideIndex, totalSlides]);

  // Apply mask to slides container
  useEffect(() => {
    if (viewportMaskRef.current && slidesContainerRef.current) {
      slidesContainerRef.current.mask = viewportMaskRef.current;
    }
  }, []);

  // Get pagination configuration (size, spacing, and position for all devices/orientations)
  const getPaginationConfig = useCallback(() => {
    const baseDotSize = 16; // Base size for desktop
    let dotSizeMultiplier = 1.0;
    let dotSpacingMultiplier = 2.0; // Spacing is 2x dot size
    let paginationYMultiplier = 0.5; // Y position multiplier

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        dotSizeMultiplier = 2.0;
        dotSpacingMultiplier = 2.0;
        paginationYMultiplier = 0;
      } else {
        // Landscape
        dotSizeMultiplier = 2.0;
        dotSpacingMultiplier = 2.0;
        paginationYMultiplier = 0.5;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        dotSizeMultiplier = 2.25;
        dotSpacingMultiplier = 2.0;
        paginationYMultiplier = 0;
      } else {
        // Landscape
        dotSizeMultiplier = 1.85;
        dotSpacingMultiplier = 2.0;
        paginationYMultiplier = 0;
      }
    } else {
      // Desktop
      dotSizeMultiplier = 1.25;
      dotSpacingMultiplier = 2.0;
      paginationYMultiplier = 1.0;
    }

    // Calculate responsive dot size
    const responsiveDotSize = baseDotSize * dotSizeMultiplier * uiSymbolScale;
    const dotSize = Math.round(responsiveDotSize);
    const dotSpacing = Math.round(dotSize * dotSpacingMultiplier);

    // Calculate pagination position
    const totalDotsWidth =
      totalSlides * dotSize + (totalSlides - 1) * dotSpacing;
    const paginationStartX = Math.round(-totalDotsWidth / 2);
    const paginationY = Math.round(
      viewportConfig.viewportHeight +
        buttonConfig.height * paginationYMultiplier
    );

    return { paginationY, paginationStartX, dotSize, dotSpacing };
  }, [
    device,
    orientation,
    uiSymbolScale,
    viewportConfig.viewportHeight,
    buttonConfig.height,
    totalSlides,
  ]);

  const paginationConfig = useMemo(
    () => getPaginationConfig(),
    [getPaginationConfig]
  );

  return (
    <>
      {/* Viewport Mask */}
      <ViewportMask
        ref={viewportMaskRef}
        width={viewportConfig.maxWidth}
        height={viewportConfig.viewportHeight}
      />

      {/* Left Arrow */}
      <SliderButton
        direction="left"
        viewportConfig={viewportConfig}
        onClick={handlePrevious}
      />

      {/* Right Arrow */}
      <SliderButton
        direction="right"
        viewportConfig={viewportConfig}
        onClick={handleNext}
      />

      {/* Slides Container - moves horizontally */}
      <Container
        ref={slidesContainerRef}
        name="paytable_slides_container"
        x={0}
        y={0}
      >
        {/* Clone of last slide, regular slides, clone of first slide */}
        {renderSlide(totalSlides - 1, 0, "clone-last")}
        {Array.from({ length: totalSlides }, (_, i) =>
          renderSlide(i, i + 1, i)
        )}
        {renderSlide(0, totalSlides + 1, "clone-first")}
      </Container>

      {/* Pagination Dots */}
      {totalSlides > 1 && (
        <Container
          name="paytable_pagination"
          x={0}
          y={paginationConfig.paginationY}
        >
          {Array.from({ length: totalSlides }).map((_, index) => {
            const dotX = Math.round(
              paginationConfig.paginationStartX +
                index *
                  (paginationConfig.dotSize + paginationConfig.dotSpacing) +
                paginationConfig.dotSize / 2
            );
            // Map clone positions to real slide indices
            const realSlideIndex =
              currentSlideIndex < 0
                ? totalSlides - 1
                : currentSlideIndex >= totalSlides
                ? 0
                : currentSlideIndex;
            const isActive = index === realSlideIndex;

            return (
              <Graphics
                key={index}
                name={`paytable_dot_${index}`}
                draw={(g) => {
                  g.clear();
                  g.beginFill(isActive ? 0xffffff : 0xfeff00, 1);
                  g.drawCircle(0, 0, paginationConfig.dotSize / 2);
                  g.endFill();
                }}
                x={dotX}
                y={0}
                eventMode="static"
                cursor="pointer"
                alpha={1}
                onpointertap={() => handleDotClick(index)}
              />
            );
          })}
        </Container>
      )}
    </>
  );
};

export default PaytableSlider;
